<template>
  <Dialog
    v-model:visible="localVisible"
    modal
    :maximizable="!isMobile"
    :closable="false"
    ref="questionDialog"
    @show="showQuestionDialog"
    @hide="cancelFormData"
    :style="{ width: '100vw', height: '100vh', maxHeight: '100vh' }"
    :breakpoints="{ '1199px': '75vw', '770px': '98vw' }"
  >
    <template #header>
      <div class="flex items-center justify-center">
        <div>
          <Button
            v-if="currentCardIndex > 0"
            icon="pi pi-arrow-left"
            severity="secondary"
            rounded
            outlined
            aria-label="Back"
            @click="navigateCard('prev')"
          />

          <span class="font-bold text-xl text-surface-950 px-3">
            {{ cardItem.title }}
          </span>
          <Button
            v-if="currentCardIndex < cardList.length - 1"
            icon="pi pi-arrow-right"
            severity="secondary"
            rounded
            outlined
            aria-label="Next"
            @click="navigateCard('next')"
          />
        </div>
      </div>
    </template>

    <PageBuilderComponent
      v-if="pageData"
      ref="pageBuilderComponent"
      :key="pageData.id"
      :page="pageData"
      :savedResValue="savedResValue"
      :conditionCheckResponse="conditionCheckResponse"
      :templateData="templateData"
      :updateLocalStore="false"
    />

    <template #footer>
      <div class="pt-3">
        <div class="flex flex-grow justify-center items-center space-x-2">
          <Button
            label="Cancel"
            icon="pi pi-times"
            text
            @click="cancelFormData"
            class="mr-2"
          />
          <Button
            label="Save"
            icon="pi pi-check"
            @click="saveFormData"
          />
          <Button
            v-if="currentCardIndex < cardList.length - 1"
            label="Save & Next"
            @click="navigateCard('next')"
            severity="contrast"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <FullScreenOverlayLoader
    :isLoading="loaderObj.isLoading"
    :message="loaderObj.loaderMsg"
  />

  <ErrorDialog
    :visible="errorObj.isVisible"
    :message="errorObj.errorMsg"
    :onConfirm="onErrorDialogClose"
    @update:visible="errorObj.isVisible = $event"
  />
</template>

<script>
  import { defineComponent, ref, watch, computed, nextTick } from "vue";
  import PageBuilderComponent from "@/components/PageBuilderComponent.vue";
  import FullScreenOverlayLoader from "@/components/FullScreenOverlayLoader.vue";
  import ErrorDialog from "@/components/ErrorDialog.vue";
  import { useFormPageStore } from "@/stores/formPageStore";
  import { useFormTemplateResponseStore } from "@/stores/formTemplateResponseStore";
  import Helper from "@/utils/helper";
  import { storeToRefs } from "pinia";

  export default defineComponent({
    name: "QuestionDialog",
    components: {
      PageBuilderComponent,
      FullScreenOverlayLoader,
      ErrorDialog
    },
    props: {
      visible: {
        type: Boolean,
        required: true
      },
      cardItem: {
        type: Object,
        required: true
      },
      cardListPageId: {
        type: String,
        required: true
      },
      cardList: {
        type: Array,
        required: true,
        default: () => []
      },
      currentCardIndex: {
        type: Number,
        required: true,
        default: 0
      },
      templateData: {
        type: Object,
        required: false,
        default: null
      }
    },
    emits: ["update:visible", "save", "navigate"],
    setup(props, { emit }) {
      const localVisible = ref(props.visible);
      const pageData = ref(null);
      const questionDialog = ref();
      const loaderObj = ref({
        isLoading: false,
        loaderMsg: ""
      });

      const errorObj = ref({
        isVisible: false,
        errorMsg: ""
      });

      const formTemplateResponseStore = useFormTemplateResponseStore();
      const { response, templateId } = storeToRefs(formTemplateResponseStore);

      const pageBuilderComponent = ref(null);
      const localFieldValues = ref({});

      const savedResValue = computed(() => {
        if (!props.cardItem?.id || !props.cardListPageId) return {};

        if (
          response.value &&
          templateId.value &&
          response.value[templateId.value]
        ) {
          const cardListData =
            response.value[templateId.value][props.cardListPageId];
          if (cardListData && cardListData[props.cardItem.id]) {
            return cardListData[props.cardItem.id];
          }
        }

        return {};
      });

      const showQuestionDialog = () => {
        questionDialog.value.maximized = true;
      };

      watch(
        () => props.visible,
        (newVal) => {
          localVisible.value = newVal;
          if (newVal && props.cardItem?.page_id) {
            loadPageData();
          }
        }
      );

      watch(localVisible, (newVal) => {
        emit("update:visible", newVal);
      });

      watch(
        () => [props.cardItem?.page_id, props.visible],
        ([newPageId, visible], [oldPageId]) => {
          if (visible && newPageId && newPageId !== oldPageId) {
            loadPageData();
          }
        }
      );

      const onErrorDialogClose = () => {
        errorObj.value.isVisible = false;
        errorObj.value.errorMsg = "";
      };

      const loadPageData = async () => {
        try {
          loaderObj.value.isLoading = true;
          loaderObj.value.loaderMsg = "Loading questions...";

          pageData.value = null;

          const formPageStore = useFormPageStore();
          const result = await formPageStore.getFormPageById(
            props.cardItem.page_id,
            true
          );

          const newPageData = {
            id: props.cardItem.page_id,
            pageObj: result?.data || result,
            is_list: false
          };

          // Set the saved values to the fields
          const savedValues = savedResValue.value;
          if (Object.keys(savedValues).length > 0) {
            // Set saved values if they exist
            newPageData.pageObj.fields.forEach((fieldGroup) => {
              fieldGroup.group.forEach((field) => {
                if (savedValues[field.id] !== undefined) {
                  field.value = savedValues[field.id];
                }
              });
            });
          } else {
            // Explicitly initialize if no saved values
            newPageData.pageObj.fields.forEach((fieldGroup) => {
              fieldGroup.group.forEach((field) => {
                field.value = null;
              });
            });
          }

          pageData.value = newPageData;

          await nextTick();
          setupFieldTracking();
        } catch (error) {
          console.error("Error loading page data:", error);
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = "Failed to load questions";
        } finally {
          loaderObj.value.isLoading = false;
        }
      };

      const conditionCheckResponse = computed(() => {
        const baseResponse = response.value[templateId.value] || {};

        return {
          ...baseResponse,
          ...localFieldValues.value
        };
      });

      const setupFieldTracking = () => {
        localFieldValues.value = {};
        // Set up watchers for all form input components to track value changes
        if (pageBuilderComponent.value?.formInputComponents) {
          pageBuilderComponent.value.formInputComponents.forEach(
            (component) => {
              if (component?.field?.id) {
                watch(
                  () => component.field.value,
                  (newValue) => {
                    localFieldValues.value[component.field.id] = newValue;
                  }
                );
              }
            }
          );
        }
      };

      // validation check
      const validatePageBeforeAction = async () => {
        let isValid = true;
        let firstInvalidField = null;

        if (pageBuilderComponent.value) {
          const validation = await pageBuilderComponent.value.validatePage();
          if (!validation.isValid) {
            isValid = false;
            firstInvalidField = validation.invalidFields[0];
          }
        }

        if (!isValid && firstInvalidField) {
          pageBuilderComponent.value.scrollToInvalidField(firstInvalidField);
          Helper.showToast(
            "Error",
            "Please fill in all required fields correctly",
            "error"
          );
        }

        return isValid;
      };

      const handleFormAction = async (action, direction = null) => {
        try {
          loaderObj.value.isLoading = true;
          loaderObj.value.loaderMsg = "Saving form...";

          const isValid = await validatePageBeforeAction();
          if (!isValid) return;

          const formData = extractFormDataFromPageBuilder();
          emit("save", {
            page_id: props.cardItem.page_id,
            title: props.cardItem.title,
            data: formData
          });

          if (action === "save") {
            Helper.showToast("Success", "Form saved successfully");
            localVisible.value = false;
          } else if (action === "navigate") {
            emit("navigate", direction);
          }
        } catch (error) {
          console.error("Save error:", error);
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = "Failed to save form";
        } finally {
          loaderObj.value.isLoading = false;
        }
      };

      const saveFormData = () => handleFormAction("save");
      const navigateCard = (direction) =>
        handleFormAction("navigate", direction);

      const extractFormDataFromPageBuilder = () => {
        // Extract all form field values from PageBuilderComponent and return as object
        const formData = {};

        if (pageBuilderComponent.value?.formInputComponents) {
          pageBuilderComponent.value.formInputComponents.forEach(
            (component) => {
              if (component?.field?.id && component.field.value !== undefined) {
                formData[component.field.id] = component.field.value;
              }
            }
          );
        }

        return formData;
      };

      const cancelFormData = () => {
        pageData.value?.pageObj?.fields?.forEach((fieldGroup) => {
          fieldGroup.group?.forEach((field) => {
            field.value = savedResValue.value[field.id] ?? null;
          });
        });

        localVisible.value = false;
      };

      const isMobile = computed(() => {
        return Helper.isMobileSize();
      });

      return {
        localVisible,
        pageData,
        questionDialog,
        showQuestionDialog,
        loaderObj,
        errorObj,
        onErrorDialogClose,
        pageBuilderComponent,
        saveFormData,
        cancelFormData,
        savedResValue,
        conditionCheckResponse,
        navigateCard,
        isMobile
      };
    }
  });
</script>
